"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var f = module.exports = {}; // eslint-disable-line no-multi-assign
var UiMoney = require('ui-money');
var dateUtility = require('./utility/dateUtility');

/**
 * Formats as money
 * @param {String} value Preformatted value
 * @return {String} formatted value
 *
 * @this Declare with bind(), lodash.bind(), or $.proxy() to add optional config
 *  @config {String} precision The preferred precision, defaults to 0
 *  @config {String} maxPrecision The maximum allowed precision, defaults to 2
 *  @config {String} currency Defaults to 'USD'
 *  @config {String} formatString Defaults to default format string for currency
 *  @config {String or Function} roundFn roundFn to pass to uiMoney, defaults to 'floor', can be 'ceil', 'round' or a function
 */
f.toMoney = function toMoney(value) {
  var prec = this.precision || 0;
  var roundFn = this.roundFn || 'floor';
  var hasDec = value.indexOf('.') !== -1;
  var suffix = '';
  if (hasDec && prec === 0) {
    // we need to increase precision based on user input
    var decSplit = value.split('.');
    var maxPrec = this.maxPrecision === 0 ? this.maxPrecision : this.maxPrecision || 2;
    prec = Math.min(decSplit[1].length, maxPrec);
    if (prec === 0 && this.maxPrecision !== 0) {
      suffix = '.'; // nothing after the decimal, but decimal exists so prep to append it to value.
    }
  }

  return new UiMoney(value, this.currency).format(prec, this.formatString, roundFn) + suffix;
};

/**
 * Formats as a human-formatted number, with thousands place separators, etc.
 * Uses number.toLocaleString() in supported browsers, returning the original value
 * in browsers that do not support toLocaleString
 *
 * @param {String} value Preformatted value
 *
 * @this Declare with bind(), lodash.bind(), or $.proxy() to add optional config
 *  @config {String} maxPrecision The maximum allowed precision, defaults to 2
 * @return {String} formatted value
 */
f.toPrettyNumber = function toPrettyNumber(value) {
  var numString = f.toNumber(value);
  if (numString === '') {
    return '';
  }
  // should use num.toLocaleString(), but that's a little fragile and
  // doesn't work on all clients.  Simplistic simple polyfill below instead.
  var decSplit = numString.split('.');
  if (decSplit.length > 1) {
    var maxPrec = this.maxPrecision === 0 ? this.maxPrecision : this.maxPrecision || 2;
    if (maxPrec === 0) {
      decSplit = [decSplit[0]];
    } else if (decSplit[1].length > maxPrec) {
      // +1 to account for decimal char
      decSplit[1] = decSplit[1].substring(0, maxPrec);
    }
  }
  decSplit[0] = decSplit[0].split(/(?=(?:\d{3})+$)/g).join(',');
  return decSplit.join('.');
};

/**
 * Formats as social security number
 * @param {String} value Preformatted value
 * @return {String} formatted value
 */
f.toSSN = function toSSN(value) {
  var num = f.toNumber(value);
  var parts;
  if (num.length > 5) {
    parts = [num.substring(0, 3), num.substring(3, 5), num.substring(5, 9)];
  } else if (num.length > 3) {
    parts = [num.substring(0, 3), num.substring(3, 5)];
  } else {
    parts = [num];
  }
  return parts.join('-');
};

/**
 * Formats a numeric string as a pure number, stripping out any non-numeric characters
 * @param {String} value Preformatted value
 * @return {String} formatted value
 */
f.toNumber = function toNumber(value) {
  var firstChar = value.charAt(0) === '-' ? '-' : '';
  return firstChar + value.replace(/[^\d\.]/g, ''); // eslint-disable-line no-useless-escape
};

/**
 * Formats as phone number
 * @param {String} value Preformatted value
 * @return {String} formatted value
 */
var phoneRE = /^(1)?(\d{0,3})?(\d{0,3})?(\d{0,4})?(\d*)?$/;
var phoneExtensionRE = /\s*[A-Za-z]+\s*/;
f.toPhone = function toPhone(value) {
  var zeroth = value.charAt(0);
  if (zeroth === '+' || zeroth === '0') {
    return value; // don't try to format non-US numbers, beginning with + or 0
  }

  // if an extension is added, split it off
  var formattedSplit = value.split(phoneExtensionRE);

  // format number part
  var num = f.toNumber(formattedSplit[0]);
  var reRes = phoneRE.exec(num);
  var formatted;
  if (reRes) {
    // we have something to format
    formatted = '';
    if (reRes[1]) {
      // '1', if it exists
      formatted += reRes[1];
    }
    if (reRes[2]) {
      // area code
      formatted += reRes[1] ? " (".concat(reRes[2]) : "(".concat(reRes[2]);
    }
    if (reRes[3]) {
      formatted += ") ".concat(reRes[3]);
    }
    if (reRes[4]) {
      formatted += "-".concat(reRes[4]);
    }
  } else {
    formatted = formattedSplit;
  }
  if (formattedSplit.length > 1) {
    // add back 'extension... anything after letters in the original
    formatted += " x".concat(formattedSplit[1]);
  }
  return formatted;
};

/**
 * Formats as date split on either '/' or '-'. Treads lightly as we can't
 * assume too much about user intention with incomplete dates.
 *
 * @param {String} value - Preformatted value
 * @return {String} formatted value
 */
f.toDate = function toDate(value) {
  var divider = '-';
  if (value.indexOf(divider) === -1) {
    // if date not split by '-', assume split by '/'
    divider = '/';
  }

  // Split each section into its own section by divider.
  // Filter to remove consecutive dividers, as in when
  // a value is formatted as '05/' and the customer blindly types another '/'.
  // Only want to filter double consecutive dividers so we don't remove valid formatting
  // that anticipates the next part of the date.
  var hasEmpty = false;
  var inputParts = value.split(divider).filter(function (part) {
    if (part.length === 0) {
      var dblEmpty = hasEmpty;
      hasEmpty = true;
      return !dblEmpty;
    }
    return true;
  });

  // initial formatting just the filtered
  var formatted = inputParts.join(divider);
  if (inputParts.length < 3) {
    // only do additional formatting if less 3 groups, otherwise just let it go.

    if (inputParts.length === 2 && inputParts[1].length === 2) {
      // if we have MM/DD entered
      var params = formatted.split(divider);
      var month = params[0].length < 2 ? "0".concat(params[0]) : params[0];
      var day = params[1].length < 2 ? "0".concat(params[1]) : params[1];
      if (dateUtility.isValidMMDD("".concat(month).concat(day))) {
        formatted = dateUtility.formatMMDD("".concat(month).concat(day), divider);
      }
    } else if (inputParts.length === 1) {
      // if no dividers assume different intents based on num characters
      if (formatted.length === 8) {
        if (dateUtility.isValidYear(formatted)) {
          formatted = dateUtility.formatMMDDYYYY(formatted, divider);
        }
      } else if (formatted.length === 4) {
        if (dateUtility.isValidMMDD(formatted)) {
          formatted = dateUtility.formatMMDD(formatted, divider);
        }
      } else if (formatted.length === 2) {
        if (dateUtility.isValidMonth(formatted)) {
          formatted = "".concat(formatted).concat(divider);
        }
      }
    }
  }
  return formatted;
};
var _default = f;
exports.default = _default;
